import React from 'react';
import { COPY } from '../../contracts/copyMap';
import { RATING_MIN, RATING_MAX } from '../../contracts/scanFeedbackApi';

// ScorePicker — the overall 1-10 rating on a judging card.
//
// ── THE PRIVACY LINE IS GONE ────────────────────────────────────────────────
//
// This used to carry "Private. Never shown to them." underneath, and the
// reasoning was calibration: told nothing, a judge may assume the number lands
// in front of the face they are looking at and rate defensively upward, which
// is the compression that makes the data worthless.
//
// Removed on request. The claim is still TRUE — nothing in the product shows an
// individual rating to the person judged; FeedbackSection aggregates traits and
// never surfaces a score — so nothing here is now misleading. What is lost is
// the reassurance, and with it the defence against upward compression.
//
// If the score distribution starts bunching at 7-8, this is the first thing to
// put back.
//
// ── WHY TEN CELLS, NOT A SLIDER ─────────────────────────────────────────────
//
// A slider suggests a continuum and invites the middle: drag-to-7 is the path
// of least resistance and the distribution collapses. Ten discrete targets make
// every value an equal, deliberate tap.
//
// It also matches UnlockProgress, which the judge has already seen at the top
// of this page. Same visual language, opposite meaning — that row REPORTS, this
// one COLLECTS — so this is a button group with real focus states rather than
// an aria-hidden decoration.
//
//   ░░ ░░ ░░ ░░ ░░ ▓▓ ░░ ░░ ░░ ░░      selected = 6
//    1  2  3  4  5  6  7  8  9 10
//
// Radios, not checkboxes: exactly one value, arrow keys move between them, and
// a screen reader announces the group name once instead of ten times.

const VALUES = Array.from(
  { length: RATING_MAX - RATING_MIN + 1 },
  (unused, i) => RATING_MIN + i,
);

export default function ScorePicker({ value, onChange, testId = 'judge-score' }) {
  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-display uppercase text-[11px] tracking-[0.2em] text-bone-dim">
          {COPY.SCAN_JUDGE_SCORE_TITLE}
        </span>
        <span className="font-display text-[11px] tracking-[0.16em] text-bone-dim tabular-nums">
          {value ? `${value} / ${RATING_MAX}` : `— / ${RATING_MAX}`}
        </span>
      </div>

      <div
        role="radiogroup"
        aria-label={COPY.SCAN_JUDGE_SCORE_TITLE}
        className="flex gap-[6px]"
      >
        {VALUES.map((n) => {
          const isSelected = value === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${n}`}
              data-testid={`judge-score-${n}`}
              data-selected={isSelected ? 'true' : 'false'}
              // Only the selected cell is in the tab order; arrow keys move
              // within the group. Ten tab stops for one question is the kind of
              // thing that makes keyboard users give up on a form.
              tabIndex={isSelected || (!value && n === RATING_MIN) ? 0 : -1}
              onClick={() => onChange(n)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  onChange(Math.min(RATING_MAX, (value || RATING_MIN) + 1));
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  onChange(Math.max(RATING_MIN, (value || RATING_MIN + 1) - 1));
                }
              }}
              className={[
                'flex-1 h-9 rounded-[3px] border font-censor text-[13px] tabular-nums',
                'transition-colors duration-150',
                isSelected
                  ? 'bg-aurum-pip border-aurum/90 text-obsidian shadow-[0_0_9px_rgba(201,164,92,0.5)]'
                  : 'bg-white/[0.06] border-white/10 text-bone-dim hover:border-aurum/50',
              ].join(' ')}
            >
              {n}
            </button>
          );
        })}
      </div>

    </div>
  );
}
